(() => {
  if (window.agHeaderInitialized) return;
  window.agHeaderInitialized = true;
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = this.closest('.section-header') || document.querySelector('.section-header');
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();

      this.headerMediaQuery = window.matchMedia('(max-width: 1023px)');
      this.onHeaderHeightChange = this.setHeaderHeight.bind(this);
      this.headerMediaQuery.addEventListener('change', this.onHeaderHeightChange);

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearches = this.querySelectorAll('.header__ag-search, .header__ag-mobile-predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.onHeaderFocus = this.reveal.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('focusin', this.onHeaderFocus);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.onHeaderFocus);
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      this.headerMediaQuery?.removeEventListener('change', this.onHeaderHeightChange);
      this.headerObserver?.disconnect();
      window.clearTimeout(this.isScrolling);
    }

    createObserver() {
      this.headerObserver = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      this.headerObserver.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      const hasOpenPredictiveSearch = Array.from(this.predictiveSearches).some(
        (predictiveSearch) =>
          predictiveSearch.isOpen ||
          predictiveSearch.classList.contains('is-open') ||
          predictiveSearch.hasAttribute('open') ||
          predictiveSearch.hasAttribute('loading')
      );

      if (hasOpenPredictiveSearch) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky || this.header.contains(document.activeElement) || this.header.classList.contains('menu-open')) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal) {
        this.searchModal.close(false);
      }
    }
  }

  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  function initAgHeaderSearch(container = document) {
    container.querySelectorAll('.header__ag-search').forEach((search) => {
      if (search.dataset.agSearchInitialized === 'true') return;

      const input = search.querySelector('input[type="search"]');
      const closeButton = search.querySelector('[data-ag-search-close]');
      const overlay = search.querySelector('[data-ag-search-overlay]');
      const form = search.querySelector('.header__ag-search-form');
      const sectionHeader = search.closest('.section-header');

      if (!input) return;

      const revealHeader = () => {
        sectionHeader?.classList.add('shopify-section-header-sticky', 'animate');
        sectionHeader?.classList.remove('shopify-section-header-hidden');
      };

      const openSearch = () => {
        revealHeader();
        search.classList.add('is-open');
        input.focus({ preventScroll: true });
      };

      const closeSearch = () => {
        search.classList.remove('is-open');

        if (typeof search.close === 'function') {
          search.close(true);
        } else {
          input.value = '';
          search.removeAttribute('open');
          search.removeAttribute('loading');
          search.removeAttribute('results');
        }

        closeButton?.classList.add('hidden');
        input.blur();
      };

      closeButton?.addEventListener('click', () => {
        window.requestAnimationFrame(closeSearch);
      });

      form?.addEventListener('click', (event) => {
        if (event.target.closest('[data-ag-search-close], [data-predictive-search]')) return;

        openSearch();
      });

      input.addEventListener('focus', () => {
        revealHeader();
        search.classList.add('is-open');
      });

      search.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!search.contains(document.activeElement)) {
            search.classList.remove('is-open');
          }
        });
      });

      overlay?.addEventListener('click', closeSearch);

      search.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') {
          closeSearch();
        }
      });

      search.dataset.agSearchInitialized = 'true';
    });

    container.querySelectorAll('.header__ag-mobile-predictive-search').forEach((search) => {
      if (search.dataset.agMobileSearchInitialized === 'true') return;

      const input = search.querySelector('input[type="search"]');
      const closeButton = search.querySelector('[data-ag-mobile-search-close]');
      const form = search.querySelector('.header__ag-mobile-search');
      const drawer = search.closest('.header__ag-mobile-drawer');
      const drawerDetails = search.closest('.header__ag-drawer-details');

      if (!input || !drawer) return;

      const syncDrawerSearchState = () => {
        const isSearchActive =
          input.value.trim().length > 0 &&
          (search.classList.contains('is-open') || search.hasAttribute('open') || search.hasAttribute('loading'));

        drawer.classList.toggle('search-active', isSearchActive);
      };

      const openSearch = () => {
        search.classList.add('is-open');
        syncDrawerSearchState();
        input.focus({ preventScroll: true });
      };

      const closeSearch = () => {
        search.classList.remove('is-open');

        if (typeof search.close === 'function') {
          search.close(true);
        } else {
          input.value = '';
          search.removeAttribute('open');
          search.removeAttribute('loading');
          search.removeAttribute('results');
        }

        closeButton?.classList.add('hidden');
        syncDrawerSearchState();
        input.blur();
      };

      const resetSearch = () => {
        search.classList.remove('is-open');

        if (search.abortController) {
          search.abortController.abort();
          search.abortController = new AbortController();
        }

        if ('searchTerm' in search) {
          search.searchTerm = '';
        }

        if (typeof search.close === 'function') {
          search.close(true);
        } else {
          input.value = '';
          search.removeAttribute('open');
          search.removeAttribute('loading');
          search.removeAttribute('results');
        }

        closeButton?.classList.add('hidden');
        syncDrawerSearchState();
      };

      const collapseSearch = () => {
        search.classList.remove('is-open');

        if (typeof search.close === 'function') {
          search.close();
        } else {
          search.removeAttribute('open');
          search.removeAttribute('loading');
        }

        closeButton?.classList.toggle('hidden', input.value.trim().length === 0);
        syncDrawerSearchState();
        input.blur();
      };

      closeButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.requestAnimationFrame(() => {
          resetSearch();
          input.focus({ preventScroll: true });
        });
      });

      form?.addEventListener('click', (event) => {
        if (event.target.closest('[data-ag-mobile-search-close], [data-predictive-search]')) return;

        openSearch();
      });

      input.addEventListener('focus', () => {
        search.classList.add('is-open');
        syncDrawerSearchState();
      });

      input.addEventListener('input', () => {
        if (input.value.trim().length) {
          search.classList.add('is-open');
        } else {
          search.classList.remove('is-open');
        }

        syncDrawerSearchState();
      });

      search.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!search.contains(document.activeElement)) {
            collapseSearch();
          }
        });
      });

      search.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('search-active')) event.preventDefault();
      });

      search.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE' && drawer.classList.contains('search-active')) {
          event.stopPropagation();
          closeSearch();
          input.focus({ preventScroll: true });
        }
      });

      drawerDetails?.addEventListener('toggle', () => {
        if (!drawerDetails.open) {
          closeSearch();
        }
      });

      search.dataset.agMobileSearchInitialized = 'true';
    });
  }

  initAgHeaderSearch();
  document.addEventListener('shopify:section:load', (event) => initAgHeaderSearch(event.target));
})();
